import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';

export interface SharedFile {
  id: string;
  filename: string;
  uri: string;
  type: 'file' | 'text' | 'multiple_files';
  content?: string;
  mimeType?: string;
  timestamp: number;
}

@Injectable({
  providedIn: 'root'
})
export class SharedFileService {
  private sharedFilesSubject = new BehaviorSubject<SharedFile[]>([]);
  public sharedFiles$ = this.sharedFilesSubject.asObservable();

  private currentSharedFileSubject = new BehaviorSubject<SharedFile | null>(null);
  public currentSharedFile$ = this.currentSharedFileSubject.asObservable();

  constructor() {
    // Load any existing shared files from storage
    this.loadSharedFiles();
  }

  addSharedFile(sharedData: any): SharedFile {
    // Use Android timestamp if available, otherwise use current time
    const timestamp = sharedData.androidTimestamp || Date.now();
    console.log('🔧 SharedFileService: Creating shared file with timestamp:', {
      timestamp,
      timestampDate: new Date(timestamp).toISOString(),
      currentTime: new Date().toISOString(),
      timeDiff: Date.now() - timestamp,
      isAndroidTimestamp: !!sharedData.androidTimestamp,
      androidTimestamp: sharedData.androidTimestamp
    });
    
    const sharedFile: SharedFile = {
      id: this.generateId(),
      filename: sharedData.filename || sharedData.name || 'unknown_file',
      uri: sharedData.content || '',
      type: sharedData.type || 'file',
      content: sharedData.content,
      mimeType: sharedData.mimeType,
      timestamp: timestamp
    };

    // Add to the list
    const currentFiles = this.sharedFilesSubject.value;
    let updatedFiles = [sharedFile, ...currentFiles]; // Add to beginning
    
    // Keep only the last 10 files to prevent storage quota issues
    if (updatedFiles.length > 10) {
      updatedFiles = updatedFiles.slice(0, 10);
      console.log('🔧 SharedFileService: Trimmed shared files to last 10 items');
    }
    
    this.sharedFilesSubject.next(updatedFiles);

    // Set as current shared file
    this.currentSharedFileSubject.next(sharedFile);

    // Save to storage (with error handling)
    this.saveSharedFiles(updatedFiles);

    console.log('🔧 SharedFileService: Added shared file:', sharedFile);
    return sharedFile;
  }

  getSharedFiles(): SharedFile[] {
    return this.sharedFilesSubject.value;
  }

  getCurrentSharedFile(): SharedFile | null {
    return this.currentSharedFileSubject.value;
  }

  clearCurrentSharedFile(): void {
    this.currentSharedFileSubject.next(null);
  }

  clearAllSharedFiles(): void {
    this.sharedFilesSubject.next([]);
    this.currentSharedFileSubject.next(null);
    this.saveSharedFiles([]);
  }

  private generateId(): string {
    return 'shared_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
  }

  private loadSharedFiles(): void {
    try {
      const stored = localStorage.getItem('sharedFiles');
      if (stored) {
        const files = JSON.parse(stored);
        this.sharedFilesSubject.next(files);
        console.log('🔧 SharedFileService: Loaded shared files from storage:', files.length);
      }
    } catch (error) {
      console.error('🔧 SharedFileService: Error loading shared files:', error);
    }
  }

  private saveSharedFiles(files: SharedFile[]): void {
    try {
      // For files with large content (data URLs), don't persist to localStorage
      // Only persist metadata, not the actual content
      const filesToSave = files.map(f => ({
        ...f,
        content: undefined, // Remove large content from storage
        uri: undefined // Remove large URIs from storage
      }));
      
      localStorage.setItem('sharedFiles', JSON.stringify(filesToSave));
      console.log('🔧 SharedFileService: Saved shared files metadata to storage:', files.length);
    } catch (error) {
      console.error('🔧 SharedFileService: Error saving shared files:', error);
      // If still quota exceeded, clear all and try again
      if (error.name === 'QuotaExceededError') {
        try {
          localStorage.removeItem('sharedFiles');
          console.log('🔧 SharedFileService: Cleared shared files due to quota exceeded');
        } catch (clearError) {
          console.error('🔧 SharedFileService: Error clearing shared files:', clearError);
        }
      }
    }
  }
}


